import { useEffect, useState, type RefObject } from "react";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";

/** The slice of the User-Agent Client Hints API this hook reads (not yet in lib.dom). */
interface NavigatorWithBrands extends Navigator {
    userAgentData?: { brands: ReadonlyArray<{ brand: string }> };
}

/** Chromium is the only engine that runs an SVG url() filter inside backdrop-filter. */
function isChromium(): boolean {
    if (typeof navigator === "undefined") return false;
    const brands = (navigator as NavigatorWithBrands).userAgentData?.brands;
    return brands?.some(({ brand }) => brand === "Chromium") ?? false;
}

/** Default light direction: top-left, matching the CSS fallback of --lg-angle. */
const DEFAULT_ANGLE = 135;

/**
 * Drives the Liquid Glass lighting on a device root.
 *
 * - Sets `--lg-angle` (deg) on `rootRef` so every glass rim and icon rim brightens on the edge
 *   facing the pointer. Updates are batched to one per animation frame and skipped entirely
 *   under reduced motion (the CSS default stays in force).
 * - Returns whether the lens refraction filter can be used; the caller puts `lg-refract` on the
 *   same root, which switches `backdrop-filter` to the SVG lens in Chromium only.
 */
export function useGlassLight(rootRef: RefObject<HTMLElement>): boolean {
    const reducedMotion = usePrefersReducedMotion();
    const [refract] = useState(isChromium);

    useEffect(() => {
        const root = rootRef.current;
        if (!root || reducedMotion) return;

        let frame = 0;
        let x = 0;
        let y = 0;

        const apply = () => {
            frame = 0;
            const rect = root.getBoundingClientRect();
            const dx = x - (rect.left + rect.width / 2);
            const dy = y - (rect.top + rect.height / 2);
            if (dx === 0 && dy === 0) return;
            // CSS gradient angles run clockwise from "to top"; the bright start sits opposite the pointer's direction.
            const angle = (Math.atan2(-dx, dy) * 180) / Math.PI;
            root.style.setProperty("--lg-angle", `${Math.round((angle + 360) % 360)}deg`);
        };

        const onMove = (event: PointerEvent) => {
            if (event.pointerType === "touch") return;
            x = event.clientX;
            y = event.clientY;
            if (!frame) frame = window.requestAnimationFrame(apply);
        };

        root.addEventListener("pointermove", onMove, { passive: true });
        return () => {
            root.removeEventListener("pointermove", onMove);
            window.cancelAnimationFrame(frame);
            root.style.setProperty("--lg-angle", `${DEFAULT_ANGLE}deg`);
        };
    }, [rootRef, reducedMotion]);

    return refract;
}
